import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, X, Plus, CheckCircle2, AlertCircle, ListChecks } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

export default function WaitListManage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");
  const [consoles, setConsoles] = useState([]);
  const [newItem, setNewItem] = useState({ title: "", console: "", catalog_id: null });
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get(`/waitlist/${id}/manage`, { params: { token } });
      setEntry(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load your wait list (link may have expired).");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); api.get("/consoles").then((r) => setConsoles(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [id, token]);

  // Autocomplete for new title
  useEffect(() => {
    if (!newItem.title || newItem.title.length < 2 || newItem.catalog_id) { setSuggestions([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/catalog/search", { params: { q: newItem.title, limit: 6 } });
        setSuggestions(r.data || []);
      } catch { setSuggestions([]); }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [newItem.title, newItem.catalog_id]);

  const removeItem = async (norm_title) => {
    if (!window.confirm("Remove this title from your wait list?")) return;
    setBusy(true); setError(""); setInfo("");
    try {
      const r = await api.post(`/waitlist/${id}/remove-item`, { token, norm_title });
      if (r.data.deleted) {
        setEntry(null);
        setInfo("Your wait list is now empty and has been removed.");
        return;
      }
      setEntry((p) => ({ ...p, items: r.data.items }));
      setInfo("Removed.");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const addItem = async () => {
    if (!newItem.title.trim()) return;
    setBusy(true); setError(""); setInfo("");
    try {
      const r = await api.post(`/waitlist/${id}/add-items`, { token, items: [newItem] });
      setEntry((p) => ({ ...p, items: r.data.items }));
      setNewItem({ title: "", console: "", catalog_id: null });
      setSuggestions([]);
      setInfo("Added to your wait list.");
      // If the item was already in stock, the server auto-fulfils; reload to surface that.
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (loading) return <Layout><div className="max-w-2xl mx-auto px-6 py-20 text-center"><Loader2 className="animate-spin mx-auto" /></div></Layout>;
  if (error && !entry) return <Layout>
    <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="wl-manage-not-found">
      <AlertCircle size={48} style={{ color: "var(--ab-danger)" }} className="mx-auto mb-4" />
      <p>{error}</p>
    </div>
  </Layout>;
  if (!entry) return <Layout>
    <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="wl-manage-empty">
      <CheckCircle2 size={48} style={{ color: "var(--ab-primary)" }} className="mx-auto mb-4" />
      <p>{info || "Your wait list is empty."}</p>
    </div>
  </Layout>;

  const isFulfilled = entry.status !== "active";

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="label-eyebrow mb-3" style={{ color: "var(--ab-primary)" }}>Your Wait List</div>
        <h1 className="font-display text-4xl font-black mb-2">Hi, {entry.first_name}</h1>
        <p className="text-sm mb-8" style={{ color: "var(--ab-text-muted)" }}>
          You signed up on {new Date(entry.created_at).toLocaleDateString()}. Use this page any time to add or remove games.
        </p>

        {isFulfilled && (
          <div className="surface p-4 mb-6 inline-flex items-start gap-2" style={{ borderLeft: "3px solid var(--ab-primary)" }} data-testid="wl-manage-fulfilled">
            <CheckCircle2 size={16} style={{ color: "var(--ab-primary)", flexShrink: 0, marginTop: 2 }} />
            <div>
              <div className="label-eyebrow mb-1" style={{ color: "var(--ab-primary)" }}>Fulfilled</div>
              <p className="text-sm">{entry.fulfilled_title} was back in stock — we emailed you on {entry.fulfilled_at && new Date(entry.fulfilled_at).toLocaleDateString()}. To wait-list more titles, head back to <a href="/wait-list" className="underline">the signup page</a>.</p>
            </div>
          </div>
        )}

        <div className="surface divide-y mb-6" style={{ borderColor: "var(--ab-border)" }} data-testid="wl-manage-items">
          {entry.items.length === 0 && <div className="px-5 py-6 text-sm" style={{ color: "var(--ab-text-muted)" }}>No titles on your list.</div>}
          {entry.items.map((it, i) => (
            <div key={i} className="flex items-center justify-between px-5 py-3" data-testid={`wl-manage-row-${i}`}>
              <div className="min-w-0 flex-1">
                <div className="font-bold">{it.title}</div>
                <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>{it.console || "Any console"}</div>
              </div>
              {!isFulfilled && (
                <button onClick={() => removeItem(it.norm_title)} disabled={busy} className="inline-flex items-center justify-center" style={{ width: 28, height: 28, border: "1px solid var(--ab-danger)", color: "var(--ab-danger)" }} data-testid={`wl-manage-remove-${i}`}>
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        {!isFulfilled && (
          <div className="surface p-5" data-testid="wl-manage-add">
            <div className="label-eyebrow mb-3" style={{ color: "var(--ab-primary)" }}>Add another game</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <input
                  className="input-terminal"
                  placeholder="Game title…"
                  value={newItem.title}
                  onChange={(e) => setNewItem((p) => ({ ...p, title: e.target.value, catalog_id: null }))}
                  data-testid="wl-manage-new-title"
                />
                {suggestions.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 surface" style={{ maxHeight: 240, overflow: "auto" }}>
                    {suggestions.map((s) => (
                      <button key={s.id} type="button"
                              onMouseDown={(e) => { e.preventDefault(); setNewItem({ title: s.title, console: s.console || "", catalog_id: s.id }); setSuggestions([]); }}
                              className="block w-full text-left px-3 py-2 text-sm hover:bg-[var(--ab-surface-2)] border-b" style={{ borderColor: "var(--ab-border)" }}>
                        <span className="font-bold">{s.title}</span>
                        <span className="ml-2 text-[10px]" style={{ color: "var(--ab-text-dim)" }}>{s.console}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <select className="input-terminal sm:max-w-[180px]" value={newItem.console} onChange={(e) => setNewItem((p) => ({ ...p, console: e.target.value }))} data-testid="wl-manage-new-console">
                <option value="">Any console</option>
                {consoles.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
              </select>
              <button onClick={addItem} disabled={busy || !newItem.title.trim()} className="btn-primary inline-flex items-center gap-2" data-testid="wl-manage-add-btn">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
              </button>
            </div>
          </div>
        )}

        {info && <div className="text-sm mt-4" style={{ color: "var(--ab-primary)" }} data-testid="wl-manage-info"><ListChecks size={12} className="inline mr-1" /> {info}</div>}
        {error && <div className="text-sm mt-4" style={{ color: "var(--ab-danger)" }} data-testid="wl-manage-error">{error}</div>}
      </div>
    </Layout>
  );
}
